const YES_NO = ["Yes", "No"];
const OWNER = ["Client", "Partner", "Joint", "Other"];
const STATES = ["ACT", "NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT"];

export const UNKNOWN = "Unknown";

export const STEPS = [
  {
    title: "About you",
    intro: "Start with the basics. Skip anything you'd rather discuss in person.",
    sections: [
      {
        title: "Who is this fact find for?",
        fields: [{ k: "has_partner", label: "Are you completing this with a partner?", type: "select", options: YES_NO }],
      },
      {
        title: "Personal details",
        fields: [
          { k: "title", label: "Title", type: "select", options: ["Mr", "Mrs", "Ms", "Miss", "Dr", "Other"], person: true },
          { k: "first_name", label: "First name", person: true },
          { k: "surname", label: "Surname", person: true },
          { k: "preferred_name", label: "Preferred name", person: true },
          { k: "dob", label: "Date of birth", type: "date", person: true },
          { k: "gender", label: "Gender", type: "select", options: ["Female", "Male", "Non-binary", "Prefer not to say"], person: true },
          { k: "marital_status", label: "Relationship status", type: "select", options: ["Single", "Married", "De facto", "Separated", "Divorced", "Widowed"], person: true },
          { k: "residency", label: "Residency status", type: "select", options: ["Australian citizen", "Permanent resident", "Temporary visa", "Other"], person: true },
          { k: "smoker", label: "Smoker", type: "select", options: YES_NO, person: true },
          { k: "health", label: "General health", type: "select", options: ["Excellent", "Good", "Fair", "Poor"], person: true },
        ],
      },
      {
        title: "Contact information",
        fields: [
          { k: "email", label: "Email", type: "email", person: true },
          { k: "mobile", label: "Mobile", type: "tel", person: true },
          { k: "home_phone", label: "Home phone", type: "tel" },
          { k: "address", label: "Home address" },
          { k: "suburb", label: "Suburb" },
          { k: "state", label: "State", type: "select", options: STATES },
          { k: "postcode", label: "Postcode", type: "number" },
          { k: "postal_address", label: "Postal address (if different)" },
          { k: "contact_method", label: "Preferred contact method", type: "select", options: ["Phone", "Email", "Text", "Any"] },
        ],
      },
      {
        title: "Dependants",
        list: {
          k: "dependants",
          item: "dependant",
          fields: [
            { k: "name", label: "Name" },
            { k: "dob", label: "Date of birth", type: "date" },
            { k: "relationship", label: "Relationship", type: "select", options: ["Child", "Stepchild", "Grandchild", "Parent", "Other"] },
            { k: "dependent_until", label: "Financially dependent until age", type: "number" },
          ],
        },
      },
    ],
  },
  {
    title: "Work & cash flow",
    intro: "Rough figures are fine. Annual amounts before tax unless noted.",
    sections: [
      {
        title: "Employment",
        fields: [
          { k: "employment_status", label: "Employment status", type: "select", options: ["Full-time", "Part-time", "Casual", "Self-employed", "Contractor", "Not working", "Retired"], person: true },
          { k: "occupation", label: "Occupation", person: true },
          { k: "employer", label: "Employer", person: true },
          { k: "years_employed", label: "Years with employer", type: "number", person: true },
          { k: "hours_per_week", label: "Hours per week", type: "number", person: true },
          { k: "retirement_age", label: "Planned retirement age", type: "number", person: true },
        ],
      },
      {
        title: "Income",
        fields: [
          { k: "salary", label: "Salary / wages (gross p.a.)", type: "money", person: true },
          { k: "bonus", label: "Bonuses / overtime (p.a.)", type: "money", person: true },
          { k: "business_income", label: "Business income (p.a.)", type: "money", person: true },
          { k: "rental_income", label: "Rental income (p.a.)", type: "money", person: true },
          { k: "investment_income", label: "Investment income (p.a.)", type: "money", person: true },
          { k: "pension_income", label: "Pension / annuity income (p.a.)", type: "money", person: true },
          { k: "centrelink", label: "Centrelink / government benefits (p.a.)", type: "money", person: true },
          { k: "other_income", label: "Other income (p.a.)", type: "money", person: true },
        ],
      },
      {
        title: "Expenses",
        fields: [
          { k: "living_expenses", label: "Living expenses (per month)", type: "money" },
          { k: "housing_cost", label: "Rent or mortgage repayments (per month)", type: "money" },
          { k: "school_fees", label: "School / childcare fees (p.a.)", type: "money" },
          { k: "other_regular", label: "Other regular commitments (per month)", type: "money" },
          { k: "emergency_fund", label: "Cash set aside for emergencies", type: "money" },
          { k: "upcoming_expenses", label: "Major expenses coming up (e.g. car, wedding, renovation)", type: "textarea" },
        ],
      },
    ],
  },
  {
    title: "Assets & liabilities",
    intro: "List what you own and what you owe. Add as many as you need.",
    sections: [
      {
        title: "Assets",
        list: {
          k: "assets",
          item: "asset",
          fields: [
            { k: "type", label: "Type", type: "select", options: ["Family home", "Investment property", "Cash / savings", "Term deposit", "Shares / ETFs", "Managed funds", "Business", "Vehicle", "Home contents", "Other"] },
            { k: "description", label: "Description" },
            { k: "owner", label: "Owner", type: "select", options: OWNER },
            { k: "value", label: "Current value", type: "money" },
            { k: "income", label: "Income it earns (p.a.)", type: "money" },
          ],
        },
      },
      {
        title: "Liabilities",
        list: {
          k: "liabilities",
          item: "liability",
          fields: [
            { k: "type", label: "Type", type: "select", options: ["Home loan", "Investment loan", "Car loan", "Personal loan", "Credit card", "HECS / HELP", "Business loan", "Other"] },
            { k: "lender", label: "Lender" },
            { k: "owner", label: "Owner", type: "select", options: OWNER },
            { k: "balance", label: "Balance owing", type: "money" },
            { k: "rate", label: "Interest rate (%)", type: "number" },
            { k: "repayments", label: "Repayments (per month)", type: "money" },
            { k: "term", label: "Years remaining", type: "number" },
          ],
        },
      },
    ],
  },
  {
    title: "Super & insurance",
    intro: "Your latest super statement will have most of these details.",
    sections: [
      {
        title: "Superannuation",
        list: {
          k: "super_accounts",
          item: "super account",
          fields: [
            { k: "fund", label: "Fund name (e.g. PSS, CSS, PSSap, AustralianSuper)" },
            { k: "type", label: "Type", type: "select", options: ["Accumulation", "Defined benefit", "Account-based pension", "SMSF", "Other"] },
            { k: "owner", label: "Member", type: "select", options: ["Client", "Partner"] },
            { k: "balance", label: "Balance or pension p.a.", type: "money" },
            { k: "employer_contrib", label: "Employer contributions (p.a.)", type: "money" },
            { k: "personal_contrib", label: "Personal / salary sacrifice contributions (p.a.)", type: "money" },
            { k: "insurance_in_super", label: "Insurance held in this fund", type: "select", options: YES_NO },
          ],
        },
      },
      {
        title: "Personal insurance",
        list: {
          k: "insurance",
          item: "policy",
          fields: [
            { k: "type", label: "Cover type", type: "select", options: ["Life", "TPD", "Trauma", "Income protection", "Private health", "Home & contents", "Other"] },
            { k: "owner", label: "Person insured", type: "select", options: ["Client", "Partner", "Joint"] },
            { k: "insurer", label: "Insurer / fund" },
            { k: "cover", label: "Cover amount (or monthly benefit)", type: "money" },
            { k: "premium", label: "Premium (p.a.)", type: "money" },
            { k: "in_super", label: "Held inside super", type: "select", options: YES_NO },
          ],
        },
      },
    ],
  },
  {
    title: "Estate & goals",
    intro: "Finally, what matters most to you.",
    sections: [
      {
        title: "Estate planning",
        fields: [
          { k: "has_will", label: "Do you have a current will?", type: "select", options: YES_NO, person: true },
          { k: "will_updated", label: "Year will was last updated", type: "number", person: true },
          { k: "executor", label: "Executor", person: true },
          { k: "power_of_attorney", label: "Enduring power of attorney", type: "select", options: YES_NO, person: true },
          { k: "guardianship", label: "Enduring guardianship", type: "select", options: YES_NO, person: true },
          { k: "binding_nomination", label: "Binding death benefit nominations on super", type: "select", options: YES_NO, person: true },
          { k: "estate_notes", label: "Anything else about your estate (e.g. trusts, guardians for children)", type: "textarea" },
        ],
      },
      {
        title: "Financial goals",
        fields: [
          { k: "goals_short", label: "Goals in the next 1–3 years", type: "textarea" },
          { k: "goals_medium", label: "Goals in 3–10 years", type: "textarea" },
          { k: "goals_long", label: "Long-term goals (10+ years)", type: "textarea" },
          { k: "retirement_income", label: "Desired retirement income (p.a., today's dollars)", type: "money" },
          { k: "risk_profile", label: "Attitude to investment risk", type: "select", options: ["Conservative", "Moderately conservative", "Balanced", "Growth", "High growth"] },
          { k: "ethical", label: "Ethical or responsible investing preferences", type: "textarea" },
          { k: "concerns", label: "Main financial worries or questions", type: "textarea" },
          { k: "other_info", label: "Anything else Kevin should know", type: "textarea" },
        ],
      },
    ],
  },
];

export const hasPartner = (answers) => answers.has_partner === "Yes";

const fmt = (v) => (v === undefined || v === null ? "" : String(v).trim());

export const buildSummary = (answers) => {
  const partner = hasPartner(answers);
  return STEPS.flatMap((step) =>
    step.sections.map((section) => {
      if (section.list) {
        const items = (answers[section.list.k] || []).map((item, i) => ({
          label: `${section.list.item[0].toUpperCase()}${section.list.item.slice(1)} ${i + 1}`,
          values: [
            section.list.fields
              .filter((f) => fmt(item[f.k]))
              .map((f) => `${f.label}: ${fmt(item[f.k])}`)
              .join(" · "),
          ],
        }));
        return { title: section.title, rows: items.filter((r) => r.values[0]) };
      }
      const rows = section.fields
        .map((f) => {
          const values = f.person
            ? [fmt(answers[`${f.k}.client`]), ...(partner ? [fmt(answers[`${f.k}.partner`])] : [])]
            : [fmt(answers[f.k])];
          return { label: f.label, values, person: !!f.person };
        })
        .filter((r) => r.values.some(Boolean));
      return { title: section.title, rows };
    })
  ).filter((s) => s.rows.length);
};
